import type {
  MapHitEvent,
  MapInteractionsConfig,
  MapInteractionTrigger,
  MapSurface,
} from '@gpx-spa/geo-domain'
import { MAP_INTERACTION_DEFAULTS, mapSurfaceLoading } from '@gpx-spa/geo-domain'
import {
  logMapInteractions,
  summarizeHitTarget,
  summarizeInteractionsConfig,
  summarizeSurface,
} from '@gpx-spa/geo-domain'

export type MapSurfaceHost = {
  showPopup(latlng: { lat: number; lng: number }, surface: MapSurface): void
  showTooltip(latlng: { lat: number; lng: number }, surface: MapSurface): void
  close(): void
  setCursorPointer(on: boolean): void
}

type ResolvedTiming = {
  openDelayMs: number
  closeDelayMs: number
  asyncLoadingThresholdMs: number
}

export function createMapInteractionController(
  host: MapSurfaceHost,
  providerLabel = 'map',
) {
  let config: MapInteractionsConfig | null = null
  let hoverGeneration = 0
  let hoverOpenTimer: ReturnType<typeof setTimeout> | null = null
  let hoverCloseTimer: ReturnType<typeof setTimeout> | null = null
  let activeTrigger: MapInteractionTrigger | null = null
  let activePresentation: MapSurface['presentation'] | null = null

  function timing(): ResolvedTiming {
    return {
      openDelayMs: config?.hover?.openDelayMs ?? MAP_INTERACTION_DEFAULTS.hoverOpenDelayMs,
      closeDelayMs: config?.hover?.closeDelayMs ?? MAP_INTERACTION_DEFAULTS.hoverCloseDelayMs,
      asyncLoadingThresholdMs: MAP_INTERACTION_DEFAULTS.asyncLoadingThresholdMs,
    }
  }

  function triggersEnabled(): MapInteractionTrigger[] {
    return config?.triggers ?? [...MAP_INTERACTION_DEFAULTS.triggers]
  }

  function clearHoverOpenTimer(): void {
    if (hoverOpenTimer !== null) {
      clearTimeout(hoverOpenTimer)
      hoverOpenTimer = null
    }
  }

  function clearHoverCloseTimer(): void {
    if (hoverCloseTimer !== null) {
      clearTimeout(hoverCloseTimer)
      hoverCloseTimer = null
    }
  }

  function clearHoverTimers(): void {
    clearHoverOpenTimer()
    clearHoverCloseTimer()
  }

  function showSurface(latlng: { lat: number; lng: number }, surface: MapSurface): void {
    logMapInteractions(providerLabel, 'showSurface', {
      ...summarizeSurface(surface),
      lat: latlng.lat,
      lng: latlng.lng,
    })
    activePresentation = surface.presentation
    if (surface.presentation === 'tooltip') {
      host.showTooltip(latlng, surface)
    } else {
      host.showPopup(latlng, surface)
    }
  }

  function closeSurface(): void {
    host.close()
    activeTrigger = null
    activePresentation = null
  }

  async function resolveAndShow(event: MapHitEvent, generation: number): Promise<void> {
    if (!config?.surface) return
    const latlng = event.latlng
    const { asyncLoadingThresholdMs } = timing()
    let loadingShown = false
    const loadingTimer = setTimeout(() => {
      if (generation !== hoverGeneration && event.trigger === 'hover') return
      loadingShown = true
      showSurface(latlng, mapSurfaceLoading)
    }, asyncLoadingThresholdMs)

    try {
      const result = await config.surface(event)
      clearTimeout(loadingTimer)
      if (event.trigger === 'hover' && generation !== hoverGeneration) {
        logMapInteractions(providerLabel, 'surface stale (generation)', {
          generation,
          hoverGeneration,
          ...summarizeHitTarget(event),
        })
        return
      }
      if (!result) {
        logMapInteractions(providerLabel, 'surface → null', summarizeHitTarget(event))
        const stickyPopup = activePresentation === 'popup'
        if (loadingShown || (activeTrigger === event.trigger && !stickyPopup)) closeSurface()
        return
      }
      logMapInteractions(providerLabel, 'surface resolved', {
        ...summarizeHitTarget(event),
        ...summarizeSurface(result),
      })
      showSurface(latlng, result)
      activeTrigger = event.trigger
    } catch (err) {
      clearTimeout(loadingTimer)
      logMapInteractions(providerLabel, 'surface error', {
        ...summarizeHitTarget(event),
        error: err instanceof Error ? err.message : String(err),
      })
      if (event.trigger === 'hover' && generation !== hoverGeneration) return
      closeSurface()
    }
  }

  async function dispatchHit(event: MapHitEvent): Promise<void> {
    if (!config) {
      logMapInteractions(providerLabel, 'dispatchHit skipped (no config)', summarizeHitTarget(event))
      return
    }
    if (!triggersEnabled().includes(event.trigger)) {
      logMapInteractions(providerLabel, 'dispatchHit skipped (trigger disabled)', {
        ...summarizeHitTarget(event),
        enabledTriggers: triggersEnabled(),
      })
      return
    }

    logMapInteractions(providerLabel, 'dispatchHit', summarizeHitTarget(event))

    if (event.trigger === 'click') {
      clearHoverTimers()
      hoverGeneration += 1
    }

    try {
      await config.onHit(event)
      logMapInteractions(providerLabel, 'onHit done', summarizeHitTarget(event))
    } catch (err) {
      logMapInteractions(providerLabel, 'onHit error', {
        ...summarizeHitTarget(event),
        error: err instanceof Error ? err.message : String(err),
      })
    }

    if (!config.surface) {
      logMapInteractions(providerLabel, 'no surface handler', summarizeHitTarget(event))
      return
    }

    if (event.trigger === 'click') {
      await resolveAndShow(event, hoverGeneration)
      return
    }

    clearHoverCloseTimer()
    const gen = ++hoverGeneration
    clearHoverOpenTimer()
    const { openDelayMs } = timing()

    logMapInteractions(providerLabel, 'hover open scheduled', {
      openDelayMs,
      generation: gen,
      ...summarizeHitTarget(event),
    })

    hoverOpenTimer = setTimeout(() => {
      hoverOpenTimer = null
      void resolveAndShow(event, gen)
    }, openDelayMs)
  }

  function scheduleHoverLeave(): void {
    if (!config) return
    if (activePresentation === 'popup') {
      logMapInteractions(providerLabel, 'hover leave ignored (sticky click popup)', {
        hoverGeneration,
      })
      clearHoverCloseTimer()
      return
    }
    logMapInteractions(providerLabel, 'hover leave scheduled', { hoverGeneration })
    clearHoverCloseTimer()
    const { closeDelayMs } = timing()
    hoverCloseTimer = setTimeout(() => {
      hoverCloseTimer = null
      hoverGeneration += 1
      clearHoverOpenTimer()
      closeSurface()
      host.setCursorPointer(false)
      try {
        config?.onHoverEnd?.()
      } catch (err) {
        logMapInteractions(providerLabel, 'onHoverEnd error', {
          error: err instanceof Error ? err.message : String(err),
        })
      }
      logMapInteractions(providerLabel, 'hover leave committed', { hoverGeneration })
    }, closeDelayMs)
  }

  function pointerEnter(): void {
    if (!config || !triggersEnabled().includes('hover')) return
    host.setCursorPointer(true)
    clearHoverCloseTimer()
  }

  return {
    setConfig(next: MapInteractionsConfig | null): void {
      config = next
      logMapInteractions(providerLabel, 'setConfig', summarizeInteractionsConfig(next))
      clearHoverTimers()
      hoverGeneration += 1
      closeSurface()
      host.setCursorPointer(false)
    },
    getConfig(): MapInteractionsConfig | null {
      return config
    },
    hasInteractions(): boolean {
      return config !== null
    },
    dispatchHit,
    scheduleHoverLeave,
    pointerEnter,
    clusterClickPolicy(): 'expand' | 'interact' {
      return config?.cluster?.onClick ?? MAP_INTERACTION_DEFAULTS.clusterOnClick
    },
  }
}

export type MapInteractionController = ReturnType<typeof createMapInteractionController>
